/**
 * Therapist Practice Settings page (Spec I).
 *
 * Therapist's own per-feature overrides + Apply Template buttons. Per-client
 * overrides UI is reachable from the existing client-detail view in a future
 * commit; this page focuses on "what's enabled for my clients by default".
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Stack, Switch, Button, Chip,
  LinearProgress, Alert, Divider, Snackbar
} from '@mui/material';
import { rawApiClient as apiClient } from '../../services/api';
import TherapistProfileCard from '../../components/therapist/TherapistProfileCard';
import AvailabilityManager from '../../components/therapist/AvailabilityManager';

const FEATURE_LABELS = {
  aiClientChat: { label: 'AI client chat', help: 'AI persona chats directly with the client between sessions.' },
  aiOnboarding: { label: 'AI-driven onboarding', help: 'Use AI for triage / intake flows.' },
  aiSuggestedGoals: { label: 'AI-suggested goals', help: 'AI proposes goals to the client.' },
  transcription: { label: 'Live session transcription', help: 'Transcribe video sessions for notes + QA.' },
  recording: { label: 'Recording retention', help: 'Retain audio/video beyond the transcript.' },
  draftPostSessionNote: { label: 'AI-drafted clinical note', help: 'Auto-draft a SOAP note after the session.' },
  tass: { label: 'TASS (clinical cockpit)', help: 'AI-assisted caseload management for the therapist.' },
  aiSupervisor: { label: 'AI Supervisor', help: 'Therapist-only clinical reasoning support.' },
  journal: { label: 'Client journal', help: 'Show the journal feature in the client UI.' },
  structuredCheckins: { label: 'Structured check-ins', help: 'Schedule mood/sleep/custom check-ins.' },
  asyncMessaging: { label: 'Async messaging', help: 'Allow text messages between sessions.' },
  inSessionAssessmentTrigger: { label: 'In-session assessment trigger', help: 'Trigger PHQ-9 / GAD-7 from inside the call.' },
  privacyPreservingQA: { label: 'Privacy-preserving QA', help: 'AI rubric review of session quality.' },
  trainingSimulation: { label: 'Training simulation', help: 'Therapist private simulation practice.' },
  liveVideo: { label: 'Live video sessions', help: 'AIMHI-hosted video calls.' }
};

function ThreeStateSwitch({ value, onChange, label, help }) {
  // value: true | false | null (null = no override, falls through to higher levels)
  const isOverridden = value === true || value === false;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1}>
      <Box>
        <Typography variant="subtitle1">
          {label}
          {isOverridden && (
            <Chip
              label={value ? 'overridden ON' : 'overridden OFF'}
              size="small"
              color={value ? 'success' : 'default'}
              variant="outlined"
              sx={{ ml: 1 }}
            />
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary">{help}</Typography>
      </Box>
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant={value === true ? 'contained' : 'outlined'}
          color="success"
          onClick={() => onChange(true)}
        >
          On
        </Button>
        <Button
          size="small"
          variant={value === false ? 'contained' : 'outlined'}
          color="warning"
          onClick={() => onChange(false)}
        >
          Off
        </Button>
        <Button
          size="small"
          variant={value === null || value === undefined ? 'contained' : 'outlined'}
          onClick={() => onChange(null)}
        >
          Inherit
        </Button>
      </Stack>
    </Stack>
  );
}

export default function TherapistPracticeSettings() {
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  async function load() {
    try {
      const [settings, tpls] = await Promise.all([
        apiClient.get('/therapist/practice-settings'),
        apiClient.get('/therapist/practice-settings/templates')
      ]);
      setData(settings.data);
      setTemplates(tpls.data?.templates || []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not load settings');
    }
  }

  useEffect(() => { load(); }, []);

  async function setOverride(key, value) {
    setSaving(true);
    try {
      await apiClient.patch('/therapist/practice-settings', { overrides: { [key]: value } });
      await load();
      setToast(`${FEATURE_LABELS[key]?.label || key} ${value === null ? 'cleared' : value ? 'enabled' : 'disabled'}`);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  async function applyTemplate(tpl) {
    if (!window.confirm(`Apply "${tpl.name}" to your default settings? This will overwrite ${Object.keys(tpl.overrides).length} feature settings.`)) return;
    setSaving(true);
    try {
      await apiClient.post(`/therapist/practice-settings/templates/${tpl.key}/apply`, { scope: 'self' });
      await load();
      setToast(`Applied template: ${tpl.name}`);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not apply template');
    } finally {
      setSaving(false);
    }
  }

  if (!data) {
    return (
      <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
        <Typography variant="h4" fontWeight={600}>Practice settings</Typography>
        <LinearProgress sx={{ mt: 3 }} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Box>
    );
  }

  const myOverrides = data.myOverrides || {};
  const featureKeys = data.featureKeys || Object.keys(FEATURE_LABELS);

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={600}>Practice settings</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Defaults for clients you work with. Use "Inherit" to fall back to the clinic-level setting; use "On" / "Off" to override for everyone you see.
            Per-client overrides live on the client's detail page.
          </Typography>
        </Box>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        {/* Therapist profile shown to new clients on the signup landing page */}
        <TherapistProfileCard token={localStorage.getItem('token')} />
        <AvailabilityManager token={localStorage.getItem('token')} />

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6">Apply a template</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Pre-baked configurations to save manual flipping. You can adjust individual flags afterward.
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              {templates.map(tpl => (
                <Card key={tpl.key} variant="outlined" sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600}>{tpl.name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                      {tpl.description}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={saving}
                      onClick={() => applyTemplate(tpl)}
                    >
                      Apply to my defaults
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6">Feature overrides</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Each feature can be left to inherit from the clinic-level configuration, or explicitly switched on or off for all your clients.
            </Typography>
            <Stack divider={<Divider />} spacing={2}>
              {featureKeys.map(key => {
                const meta = FEATURE_LABELS[key] || { label: key, help: '' };
                const current = key in myOverrides ? myOverrides[key] : null;
                return (
                  <ThreeStateSwitch
                    key={key}
                    label={meta.label}
                    help={meta.help}
                    value={current}
                    onChange={(v) => setOverride(key, v)}
                  />
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast}
      />
    </Box>
  );
}
